"use client";

import { useEffect } from "react";

import { isChunkLoadError } from "@/lib/observability/expected-errors";

/**
 * Next.js global error boundary. Catches errors in root layout/template.
 * Reports to Rollbar and PostHog via stratum (no Rollbar Provider here).
 * Must render its own <html> and <body>; root layout is not mounted.
 * ChunkLoadError: shown when a JS chunk fails to load (e.g. after deploy);
 * user must full-page refresh to get new assets.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Skip Rollbar for ChunkLoadError: usually deploy timing (user on old build) or
    // transient network; not a bug. User already sees "Update required" + refresh.
    if (isChunkLoadError(error)) return;

    void import("@/lib/observability/events").then(({ EventPublisher }) => {
      return import("@/lib/observability/catalog").then(({ EventKeys }) => {
        void EventPublisher.publishError(EventKeys.APP_GLOBAL_ERROR, error, {
          digest: error.digest,
        });
      });
    });
  }, [error]);

  const handleAction = () => {
    if (isChunkError) {
      window.location.reload();

      return;
    }
    reset();
  };

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "system-ui, sans-serif",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>
          {isChunkError ? "Update required" : "Something went wrong"}
        </h1>
        <p style={{ color: "#666", marginBottom: "1.5rem" }}>
          {isChunkError
            ? "A new version of the app is available. Please refresh the page to continue."
            : "We've been notified. You can try again."}
        </p>
        <button
          style={{
            backgroundColor: "#0066cc",
            border: "none",
            borderRadius: "4px",
            color: "white",
            cursor: "pointer",
            fontSize: "1rem",
            padding: "0.5rem 1rem",
          }}
          type="button"
          onClick={handleAction}
        >
          {isChunkError ? "Refresh page" : "Try again"}
        </button>
      </body>
    </html>
  );
}
